import { ProductWidgetMapping } from "./raffle-ai-types";
import { ProductDropdownModel } from "@rhinestone/portal-web-api";

export function getLoadedWidgetId() {
  const script = document.getElementById("raffle-sdk");
  return script?.getAttribute("data-uid") ?? undefined;
}

export function isWidgetLoaded() {
  return window.raffleApi !== undefined;
}

export function destroyWidget() {
  if (window.raffleApi?.destroy) {
    // it's hardcoded magic guid used to destroy a widget. It does not relate to actual widget tool, but was provided by Raffle.ai developers
    window.raffleApi.destroy("d40cbaa9-420f-4b79-9fc3-ce4c7ce13189");
  }
}

export function loadWidget(widgetId: string) {
  const script = document.createElement("script");
  script.id = "raffle-sdk";
  script.src = "https://cdn.raffle.ai/search/index.js";
  script.setAttribute("data-uid", widgetId);
  script.setAttribute("defer", "");
  document.head.appendChild(script);
}

function hasUserAccessToRaffleProducts(
  userProducts: ProductDropdownModel[] | undefined,
  productWidgetMapping: ProductWidgetMapping[]
) {
  const enabledProductCodes = productWidgetMapping.map(m =>
    m.productCode.toLowerCase()
  );
  const userProductCodes = userProducts?.map(p => p.code.toLowerCase()) ?? [];
  return enabledProductCodes.some(code => userProductCodes.includes(code));
}

function getDesiredWidgetId(
  productCode: string | undefined,
  mapping: ProductWidgetMapping[]
) {
  return productCode
    ? mapping.find(
        m => m.productCode.toLowerCase() === productCode.toLowerCase()
      )?.widgetId
    : undefined;
}

function destroyWidgetIfNeeded() {
  if (isWidgetLoaded()) {
    destroyWidget();
  }
}

function switchToWidget(desiredWidgetId: string) {
  const currentWidgetId = getLoadedWidgetId();

  if (currentWidgetId === desiredWidgetId) {
    return;
  }

  if (isWidgetLoaded()) {
    destroyWidget();
  }

  loadWidget(desiredWidgetId);
}

/**
 * Ensures the correct Raffle widget is loaded based on product context and user access.
 *
 * This is the central widget lifecycle management function that handles:
 * - User product access validation
 * - Widget loading/switching based on product code
 * - Widget destruction when no longer needed
 *
 * @param productCode - The current product code (undefined if no product context)
 * @param mapping - Product to widget ID mappings from portal configuration
 * @param userProducts - The products the current user has access to
 */
export function ensureCorrectWidget(
  productCode: string | undefined,
  mapping: ProductWidgetMapping[],
  userProducts: ProductDropdownModel[] | undefined
) {
  // Check if user has access to any Raffle-enabled products
  if (!hasUserAccessToRaffleProducts(userProducts, mapping)) {
    destroyWidgetIfNeeded();
    return;
  }

  // Find the desired widget ID for the current product
  const desiredWidgetId = getDesiredWidgetId(productCode, mapping);

  // If no widget is configured for this product, destroy any loaded widget
  if (!desiredWidgetId) {
    destroyWidgetIfNeeded();
    return;
  }

  // Switch to the desired widget (or keep current if already correct)
  switchToWidget(desiredWidgetId);
}
